import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff, Shield, KeyRound } from "lucide-react";

import { PassAlgarveLogo } from "@/components/PassAlgarveLogo";
import Seo from "@/components/Seo";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, complete2FALogin } = useAuth();
  const { t, language } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // 2FA state
  const [twofaStage, setTwofaStage] = useState(null); // null | "code" | "setup"
  const [tempToken, setTempToken] = useState("");
  const [twofaCode, setTwofaCode] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = await login(email, password);

      if (result?.twofaChallenge) {
        const ch = result.twofaChallenge;
        setTempToken(ch.temp_token);
        if (ch.requires_2fa_setup) {
          // Send to setup page
          navigate("/admin/2fa-setup", { state: { temp_token: ch.temp_token, email: ch.email } });
          return;
        }
        // Regular 2FA login
        setTwofaStage("code");
        toast.info(t("authx.enter2faToast"));
        return;
      }

      const user = result;
      toast.success(`${t("authx.welcome")}, ${user.name}!`);
      if (user.role === "admin") navigate("/admin");
      else if (user.role === "partner") navigate("/partner");
      else navigate("/dashboard");
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || t("authx.loginError"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const user = await complete2FALogin(tempToken, twofaCode);
      toast.success(`${t("authx.welcome")}, ${user.name}!`);
      if (user.role === "admin") navigate("/admin");
      else if (user.role === "partner") navigate("/partner");
      else navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || t("authx.invalidCode"));
    } finally {
      setIsLoading(false);
    }
  };

  const cancelTwoFa = () => {
    setTwofaStage(null);
    setTempToken("");
    setTwofaCode("");
  };

  return (
    <div className="min-h-screen bg-navy-dark flex flex-col">
      <Seo noindex title="Entrar na Conta" />
      <div className="flex-1 flex items-center justify-center p-4 relative">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url(https://customer-assets.emergentagent.com/job_algarve-pass-portal/artifacts/ejhtb72b_FOTO_02.png)`
          }}
        />

        <motion.div
          className="w-full max-w-md relative z-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="text-white/70 hover:text-white mb-6 -ml-2"
          data-testid="back-home-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t("authx.back")}
        </Button>

        <Card className="bg-navy border-gold/20 shadow-2xl">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <PassAlgarveLogo size="default" />
            </div>
            <CardTitle className="font-heading text-2xl text-white">
              {twofaStage === "code"
                ? (language === "en" ? "2FA Verification" : "Verificação 2FA")
                : (language === "en" ? "Sign In" : "Entrar")}
            </CardTitle>
            <CardDescription className="text-white/60 font-body">
              {twofaStage === "code"
                ? (language === "en" ? "Enter the code from Google Authenticator" : "Insira o código do Google Authenticator")
                : (language === "en" ? "Access your PASS ALGARVE account" : "Acesse sua conta do PASS ALGARVE")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {twofaStage === "code" ? (
              <form onSubmit={handleVerify2FA} className="space-y-4">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center">
                    <Shield className="w-8 h-8 text-gold" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="twofa-code" className="text-white/80 font-body">{t("authx.codeLabel")}</Label>
                  <Input
                    id="twofa-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="000000"
                    value={twofaCode}
                    onChange={(e) => setTwofaCode(e.target.value)}
                    required
                    maxLength={20}
                    className="bg-navy-dark border-gold/30 text-white placeholder:text-white/40 focus:border-gold focus:ring-gold/20 text-center text-2xl tracking-widest"
                    data-testid="twofa-code-input"
                    autoFocus
                  />
                  <p className="text-white/40 text-xs font-body">
                    {t("authx.backupHintA")}<strong>{t("authx.backupHintB")}</strong>{t("authx.backupHintC")}
                  </p>
                </div>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-gold hover:bg-gold-dark text-white font-semibold py-5 rounded-full mt-4"
                  data-testid="twofa-verify-btn"
                >
                  {isLoading ? t("authx.verifying") : t("authx.verify")}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={cancelTwoFa}
                  className="w-full text-white/60 hover:text-white"
                  data-testid="twofa-cancel-btn"
                >
                  {t("authx.cancel")}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/80 font-body">{t("authx.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t("authx.emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-navy-dark border-gold/30 text-white placeholder:text-white/40 focus:border-gold focus:ring-gold/20"
                  data-testid="login-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-white/80 font-body">{t("authx.password")}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="********"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-navy-dark border-gold/30 text-white placeholder:text-white/40 focus:border-gold focus:ring-gold/20 pr-10"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gold hover:bg-gold-dark text-white font-semibold py-5 rounded-full mt-6"
                data-testid="login-submit-btn"
              >
                {isLoading ? t("authx.loggingIn") : t("authx.login")}
              </Button>
            </form>
            )}

            {twofaStage !== "code" && (
            <div className="mt-6 text-center">
              <p className="text-white/60 font-body text-sm">
                {t("authx.noAccount")}{" "}
                <Link to="/register" className="text-gold hover:text-gold-light transition-colors" data-testid="register-link">
                  {t("authx.createAccount")}
                </Link>
              </p>
            </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
      </div>
    </div>
  );
}
